import shelflixLogoWhite from '@/assets/shelflix-logo-white.png';
import shelflixLogo from '@/assets/shelflix-logo.png';
import { RestrictedComponent } from '@/layout/RestrictedComponent';
import { useMaterialEditStore } from '@/screens/MaterialEdit/store';
import { useHeaderStore } from '@/store/headerStore';
import { useDeviceDetect } from '@/utils/useMediaQuery';
import { cn } from '@/utils/utils';
import Icon from '@shelflix/ui-web/components/Icon';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from '@shelflix/ui-web/components/ui/breadcrumb';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent } from '@shelflix/ui-web/components/ui/card';
import { useSidebar } from '@shelflix/ui-web/components/ui/sidebar';
import { Skeleton } from '@shelflix/ui-web/components/ui/skeleton';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useGenericFilter } from './GenericFilter/context';
import { useMemo, useState, useCallback, useEffect } from 'react';
import type { ReactNode } from 'react';
import { isImagePreloaded, markImageAsPreloaded } from '@/utils/imagePreloadCache';

export const Header = ({ logo }: { logo?: string }) => {
  const { title, breadcrumb, button, gradient, backButton } = useHeaderStore();
  const { setOpenMobile } = useSidebar();
  const { isMobile } = useDeviceDetect();
  const { hasFilter, setOpenMobile: setFilterOpen } = useGenericFilter();
  const navigate = useNavigate();
  const { openModal, setOpenModal } = useMaterialEditStore();
  const [modalState, setModalState] = useState<Record<number, boolean>>({});

  // Determine if we have a valid custom logo URL
  const hasCustomLogo = Boolean(logo && logo.trim());

  // Check if the logo is already preloaded
  const isPreloaded = useMemo(() => {
    if (!hasCustomLogo) return true; // Fallback logos are bundled
    return isImagePreloaded(logo);
  }, [hasCustomLogo, logo]);

  // Track loading state for this component instance
  const [localLoaded, setLocalLoaded] = useState(isPreloaded);
  const [logoError, setLogoError] = useState(false);

  // Reset error state when logo URL changes (but keep loaded state to prevent flicker)
  useEffect(() => {
    setLogoError(false);
    // Only reset localLoaded if the new logo is not preloaded
    if (!isPreloaded) {
      setLocalLoaded(isPreloaded);
    }
  }, [logo, isPreloaded]);

  // Calculate effective logo source
  const logoSrc = useMemo(() => {
    if (!hasCustomLogo || logoError) {
      return shelflixLogo;
    }
    return (logo ?? '').trim();
  }, [logo, hasCustomLogo, logoError]);

  // Handle logo load success
  const handleLogoLoad = useCallback(() => {
    setLocalLoaded(true);
    setLogoError(false);
    if (hasCustomLogo && logo) {
      markImageAsPreloaded(logo);
    }
  }, [logo, hasCustomLogo]);

  // Handle logo load error
  const handleLogoError = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
    setLogoError(true);
    setLocalLoaded(true);
    e.currentTarget.src = shelflixLogo;
  }, []);

  // Logo is loaded if preloaded or loaded locally
  const logoLoaded = isPreloaded || localLoaded;

  const poweredByLogoSrc = useMemo(() => {
    return gradient ? shelflixLogoWhite : shelflixLogo;
  }, [gradient]);

  const setModalOpen = useCallback((index: number, open: boolean) => {
    setModalState((prev) => ({ ...prev, [index]: open }));
  }, []);

  const handleButtonClick = useCallback(
    (
      btn: { onClick: () => void; modal?: (args: { open: boolean; setOpen: (open: boolean) => void }) => ReactNode },
      index: number
    ) => {
      if (btn.modal) {
        const nextOpen = !modalState[index];
        setModalOpen(index, nextOpen);
      }
      btn.onClick?.();
    },
    [modalState, setModalOpen]
  );
  return (
    <header
      className={cn(
        `z-20 w-full bg-gradient-to-b from-[var(--primary)] md:pl-[calc(16px+var(--sidebar-width))] ${gradient ? 'to-transparent' : 'to-[var(--primary)]'}`
      )}
    >
      <div className="flex w-full flex-col items-start px-2 pt-2 pb-2 sm:px-6 md:pb-2">
        <Card className="flex w-full flex-row rounded-[10px] px-0 py-2 md:hidden">
          <CardContent className="flex-1 px-2">
            <div className="flex h-[30px] w-full items-center justify-between">
              <Icon
                name="menu"
                className={'text-[var(--gray-primary)]'}
                size={24}
                onClick={() => setOpenMobile(true)}
              />
              <div className="flex flex-col items-center">
                {/* Fixed height container to prevent layout shift */}
                <div className="relative flex h-[25px] w-[100px] items-center justify-center">
                  {!logoLoaded && <Skeleton className="absolute h-[25px] w-[80px] rounded" />}
                  <img
                    src={logoSrc}
                    alt="Logo"
                    className={cn(
                      'max-h-[25px] object-contain transition-opacity duration-200',
                      logoLoaded ? 'opacity-100' : 'opacity-0'
                    )}
                    onLoad={handleLogoLoad}
                    onError={handleLogoError}
                  />
                </div>
                <div className="flex items-center gap-1">
                  <span className="ml-2 text-[5px]">Powered by</span>
                  <img className="h-[10px]" alt="Group" src={poweredByLogoSrc} />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
        <div className="flex w-full items-center justify-between gap-2 md:gap-4">
          <div className="flex w-full flex-col">
            {!isMobile && (
              <h1
                className={`mt-4 font-['Poppins'] tracking-[0] text-white sm:mt-0 md:ml-0 md:leading-[40px] ${isMobile ? 'text-lg font-medium' : 'text-3xl font-semibold'}`}
              >
                {title}
              </h1>
            )}
            {!isMobile && (
              <Breadcrumb className="my-1.5 min-h-[24px] md:my-2 md:mb-0 md:min-h-[44px]">
                <BreadcrumbList>
                  {breadcrumb.map((item, index) => {
                    const isLast = index === breadcrumb.length - 1;
                    return (
                      <div key={index} className="flex items-center gap-2">
                        <BreadcrumbItem className="text-white">
                          {item.href && !isLast ? (
                            <BreadcrumbLink className="md:text-md font-[Figtree]" href={item.href}>
                              {item.label}
                            </BreadcrumbLink>
                          ) : (
                            <span className="md:text-md md:font-[Figtree]">{item.label}</span>
                          )}
                        </BreadcrumbItem>
                        {!isLast && <BreadcrumbSeparator className="text-white" />}
                      </div>
                    );
                  })}
                </BreadcrumbList>
              </Breadcrumb>
            )}
          </div>
          <div className={cn('flex flex-col gap-2', isMobile ? 'mt-4' : 'mt-2')}>
            {button &&
              button.length > 0 &&
              !isMobile &&
              button.map((btn, idx) => (
                <RestrictedComponent
                  key={idx}
                  requiredScopes={btn?.requiredScopes ?? []}
                  allowMaster={btn?.allowMaster}
                >
                  <Button
                    // style={{ backgroundColor: isMobile ? '' : 'var(--primary)' }}
                    variant={btn.style ? btn.style : gradient ? 'default' : 'secondary'}
                    onClick={() => handleButtonClick(btn, idx)}
                    className="w-full"
                  >
                    {btn.iconName && <Icon name={btn.iconName} fill={isMobile ? 1 : btn.iconFill} size={20} />}
                    <span>{btn.label}</span>
                  </Button>
                  {btn.modal?.({
                    open: Boolean(modalState[idx]),
                    setOpen: (open) => setModalOpen(idx, open),
                  })}
                </RestrictedComponent>
              ))}
          </div>
        </div>
        <div className={cn('flex flex-row items-start justify-start gap-2', isMobile ? 'mt-4' : 'mt-2')}>
          {backButton && (
            <Button
              className={`cursor-pointer border-white px-1 text-white md:my-0`}
              variant={'outline'}
              onClick={() =>
                openModal
                  ? setOpenModal(null)
                  : navigate(breadcrumb[breadcrumb.length - 2].href, {
                      replace: true,
                      state: {},
                    })
              }
            >
              <ArrowLeft size={20} />
              <span>Voltar</span>
            </Button>
          )}

          {isMobile &&
            button &&
            button.length > 0 &&
            button.map((btn, idx) => (
              <RestrictedComponent key={idx} requiredScopes={btn?.requiredScopes ?? []} allowMaster={btn?.allowMaster}>
                <Button
                  variant={btn.style ? btn.style : gradient ? 'default' : 'secondary'}
                  onClick={() => handleButtonClick(btn, idx)}
                >
                  {btn.iconName && <Icon name={btn.iconName} fill={isMobile ? 1 : btn.iconFill} size={20} />}
                  <span>{btn.label}</span>
                </Button>
                {btn.modal?.({
                  open: Boolean(modalState[idx]),
                  setOpen: (open) => setModalOpen(idx, open),
                })}
              </RestrictedComponent>
            ))}

          {isMobile && hasFilter && (
            <Button
              className={`cursor-pointer border-white px-1 text-white md:my-0`}
              variant={'outline'}
              onClick={() => {
                setFilterOpen(true);
              }}
            >
              <Icon name="filter_alt" fill={1} className={cn('text-white', gradient && 'text-primary')} size={28} />
            </Button>
          )}
        </div>
      </div>
    </header>
  );
};
